import VectorSource from "ol/source/Vector";
import { getLocalizedText } from "../common/translations";
import ControlBase, { IControlBaseOptions } from "./controlBase";
import { Feature, Geolocation } from "ol";
import VectorLayer from "ol/layer/Vector";
import { Style, Stroke, Fill, RegularShape } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { Point } from "ol/geom";

export enum LocationIconStates {
    Disabled,
    Searching,
    Fixed,
    Error
}

export default class LocationControl extends ControlBase {
    private isTracking: boolean;
    private goToButton: HTMLButtonElement;
    private geolocation: Geolocation;
    private positionRing: Point;
    private accuracyRing: Feature;
    private headingDirection: Feature;
    private geolocationLayer: VectorLayer<VectorSource>;
    private geolocationInitialized: boolean;

    constructor(options: IControlBaseOptions) {
        let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        // custom ikona
        svg.innerHTML = `<path d="M 7.4980469,1.5 V 2.6015625 C 4.8870006,2.8520782 2.8520782,4.8870006 2.6015625,7.4980469 H 1.5 v 1.0039062 h 1.1015625 c 0.2490154,2.6126529 2.284293,4.6478119 4.8964844,4.8984379 V 14.5 H 8.5019531 V 13.400391 C 11.115751,13.151266 13.151266,11.115751 13.400391,8.5019531 H 14.5 V 7.4980469 H 13.400391 C 13.149765,4.8858557 11.114606,2.8505779 8.5019531,2.6015625 V 1.5 Z M 8.0019531,3.5019531 C 10.492614,3.5019533 12.5,5.5112926 12.5,8.0019531 12.5,10.492614 10.492614,12.5 8.0019531,12.5 c -2.4906606,0 -4.4999998,-2.007386 -4.5,-4.4980469 0,-2.4906607 2.0093393,-4.5 4.5,-4.5 z"/>
            <path id="locationTrackingDisabled" d="M 12.646484,2.6464844 2.6464844,12.646484 3.3535156,13.353516 13.353516,3.3535156 Z"/>
            <path id="locationFixed" style="display: none" d="M 11,8 A 3,3 0 0 1 8,11 3,3 0 0 1 5,8 3,3 0 0 1 8,5 3,3 0 0 1 11,8 Z"/>
            <path id="locationSearching" style="display: none" d="M 8.0058594,4.5 C 7.0981193,4.4890634 6.408134,4.8232241 6.0097656,5.3320312 5.6113973,5.8408384 5.4980469,6.4579597 5.4980469,7 h 1.0039062 c 0,-0.3946374 0.091899,-0.7870267 0.296875,-1.0488281 C 7.0038044,5.6893704 7.3152634,5.4918208 7.9941406,5.5 8.688149,5.5083616 9.0059874,5.6956844 9.203125,5.9375 9.4002626,6.1793156 9.4938178,6.5515763 9.4980469,7.0039062 9.4989692,7.1030168 9.4513829,7.2295053 9.3027344,7.4121094 9.1540858,7.5947134 8.9205137,7.8059138 8.6660156,8.0390625 8.1570195,8.5053598 7.4980469,9.0965179 7.4980469,10 h 1.0039062 c 0,-0.3808431 0.3464159,-0.7686681 0.8398438,-1.2207031 C 9.5885108,8.5532794 9.8546364,8.3218607 10.080078,8.0449219 10.30552,7.767983 10.505881,7.4181528 10.501953,6.9960937 10.49653,6.4160348 10.384263,5.7980416 9.9804688,5.3027344 9.5766748,4.8074272 8.8984683,4.5107544 8.0058594,4.5 Z m 0.4960937,5.996094 -1.0039062,0.0078 0.00977,1 1.0019531,-0.0078 z" />
            <path id="locationError" style="display: none" d="M 7.5,4.5 V 10 H 8.503906 V 4.5 Z m 0,6 v 1 h 1.003906 v -1 z"/>`;
        svg.setAttribute("width", "24px");
        svg.setAttribute("height", "24px");
        svg.setAttribute("viewBox", "0 0 16 16");
        options.symbol = svg;
        options.tooltipText = getLocalizedText("controlLocationDisabled");
        super(options);

        this.goToButton = document.createElement("button");
        this.goToButton.type = "button";
        this.goToButton.classList.add("location-button");
        svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        // Bootstrap ikona Pin map - https://icons.getbootstrap.com/icons/pin-map/
        svg.innerHTML = `<path d="M3.1 11.2a.5.5 0 0 1 .4-.2H6a.5.5 0 0 1 0 1H3.75L1.5 15h13l-2.25-3H10a.5.5 0 0 1 0-1h2.5a.5.5 0 0 1 .4.2l3 4a.5.5 0 0 1-.4.8H.5a.5.5 0 0 1-.4-.8l3-4z"/>
            <path d="M8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM4 4a4 4 0 1 1 4.5 3.969V13.5a.5.5 0 0 1-1 0V7.97A4 4 0 0 1 4 3.999z"/>`;
        svg.setAttribute("width", "24px");
        svg.setAttribute("height", "24px");
        svg.setAttribute("viewBox", "0 0 16 16");
        svg.classList.add("symbol");
        this.goToButton.appendChild(svg);
        this.createTooltip(getLocalizedText("controlGoToLocation"), this.container, this.goToButton, true, "right");
        this.collapsibleContent.replaceWith(this.goToButton);

        this.button.addEventListener("click", this.toggleLocationTracking.bind(this));
        this.goToButton.addEventListener("click", this.goToCurrentLocation.bind(this));

        this.positionRing = new Point([0,0]);
        this.accuracyRing = new Feature([]);
        this.headingDirection = new Feature({ geometry: this.positionRing });
        this.headingDirection.setId("heading");
        let source = new VectorSource();
        source.addFeature(this.accuracyRing);
        source.addFeature(new Feature(this.positionRing));
        source.addFeature(this.headingDirection);
        this.geolocationLayer = new VectorLayer({
            source: source,
            style: function (f) {
                let s = new Style({
                    stroke: new Stroke({
                        color: '#bb2121',
                        width: 2,
                        lineDash: [2,5]
                    }),
                    fill: new Fill({
                        color: "#bb212133"
                    })
                });
                if (f.getId() == "heading" && typeof(f.get("heading")) !== "undefined") {
                    s.setImage(new RegularShape({
                        points: 3,
                        displacement: [0, 10],
                        radius: 5,
                        fill: new Fill({
                            color: '#bb2121'
                        }),
                        rotation: f.get("heading")
                    }));
                } else {
                    s.setImage(new CircleStyle({
                        radius: 5,
                        fill: new Fill({
                            color: '#bb2121'
                        })
                    }));
                }
                return s;
            }
        });
        this.geolocationInitialized = false;
    }

    public updateButtonState(state :LocationIconStates) {
        switch (state) {
            case LocationIconStates.Disabled:
                document.getElementById("locationTrackingDisabled").style.display = "";
                document.getElementById("locationFixed").style.display = "none";
                document.getElementById("locationSearching").style.display = "none";
                document.getElementById("locationError").style.display = "none";
                this.tooltipTextDiv.innerText = getLocalizedText("controlLocationDisabled");
                this.goToButton.classList.remove("location-available");
                break;
            case LocationIconStates.Fixed:
                document.getElementById("locationTrackingDisabled").style.display = "none";
                document.getElementById("locationFixed").style.display = "";
                document.getElementById("locationSearching").style.display = "none";
                document.getElementById("locationError").style.display = "none";
                this.tooltipTextDiv.innerText = getLocalizedText("controlLocationFixed");
                this.goToButton.classList.add("location-available");
                break;
            case LocationIconStates.Searching:
                document.getElementById("locationTrackingDisabled").style.display = "none";
                document.getElementById("locationFixed").style.display = "none";
                document.getElementById("locationSearching").style.display = "";
                document.getElementById("locationError").style.display = "none";
                this.tooltipTextDiv.innerText = getLocalizedText("controlLocationSearching");
                this.goToButton.classList.remove("location-available");
                break;
            case LocationIconStates.Error:
                document.getElementById("locationTrackingDisabled").style.display = "none";
                document.getElementById("locationFixed").style.display = "none";
                document.getElementById("locationSearching").style.display = "none";
                document.getElementById("locationError").style.display = "";
                this.tooltipTextDiv.innerText = getLocalizedText("controlLocationError");
                this.goToButton.classList.remove("location-available");
                break;
        }
    }

    private initializeGeolocation() {
        let map = this.getMap();
        if (!this.geolocationInitialized && map) {
            map.addLayer(this.geolocationLayer);
            this.geolocation = new Geolocation({
                tracking: false,
                trackingOptions: {
                    enableHighAccuracy: true
                },
                projection: map.getView().getProjection()
            });
            this.geolocation.on("change", this.updatePosition.bind(this));
            this.geolocation.on("error", this.geolocationFailed.bind(this));
            this.geolocationInitialized = true;
        }
    }

    private toggleLocationTracking() {
        this.initializeGeolocation();
        this.isTracking = !this.isTracking;
        if (this.isTracking) {
            this.updateButtonState(LocationIconStates.Searching);
        } else {
            this.updateButtonState(LocationIconStates.Disabled);
        }
        this.geolocation.setTracking(this.isTracking);
        this.geolocationLayer.setVisible(this.isTracking);
    }

    private goToCurrentLocation() {
        let map = this.getMap();
        if (this.geolocation && map) {
            map.getView().animate({
                center: this.geolocation.getPosition()
            });
        }
    }
    
    private updatePosition() {
        let position = this.geolocation.getPosition();
        this.accuracyRing.setGeometry(this.geolocation.getAccuracyGeometry());
        this.positionRing.setCoordinates(position);
        this.headingDirection.setProperties({"heading": this.geolocation.getHeading()});
        this.updateButtonState(LocationIconStates.Fixed);
    }
    
    private geolocationFailed(error: any) {
        switch (error.code) {
            case 1: //PERMISSION_DENIED
            case 2: //POSITION_UNAVAILABLE
                this.geolocationLayer.setVisible(false);
                this.updateButtonState(LocationIconStates.Error);
            break;
            case 3: //TIMEOUT
                this.geolocationLayer.setVisible(true);
                this.updateButtonState(LocationIconStates.Searching);
            break;
        }
        console.log(error);
    }    
}